import { unrefElement, useEventListener, useNow } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { HttpStatus } from "#/shared/lib/http";
import { reportClientError } from "#ce/utils/error-report";
import { defineScopedStore } from "#ce/utils/store";
import { CHECKOUT_SCOPE } from "#ce/components/SpCheckout/store/scope";
import { createPurchaseApi } from "#ce/components/SpCheckout/api/purchase";
import { prepareEligibilityValidation } from "#ce/components/SpCheckout/utils/form";
import { PaymentErrorCode } from "#ce/components/SpPayment/lib/error";

const KEY_FILTER = "registration[fields]";
/** Each match is one `[segment]` in a nested form key; capture group 1 is the segment name. */
const BRACKETED_FORM_KEY_SEGMENT_REGEX = /\[([^\]]+)\]/g;
const ONE_MINUTE = 60;
// Stripe error types a registrant causes and can fix themselves: an incomplete or invalid field, and
// a declined card. Declines are recorded server-side from the payment_intent.payment_failed webhook.
const REGISTRANT_PAYMENT_ERROR_TYPES = new Set(["validation_error", "card_error"]);
// Payment error codes a registrant causes and can fix themselves: a failed or abandoned card
// authentication (3DS), which Stripe types as invalid_request_error; InstaMed's Continue with no card
// added; and an InstaMed decline, which the server reports when it refuses the capture.
const REGISTRANT_PAYMENT_ERROR_CODES = new Set([
  "payment_intent_authentication_failure",
  PaymentErrorCode.NO_CARD_INFO,
  PaymentErrorCode.CAPTURE_DECLINED,
]);
/** Capture group 1 is the intent id (`pi_…` or `seti_…`) a Stripe client secret starts with. */
const STRIPE_INTENT_ID_REGEX = /^((?:pi|seti)_[A-Za-z0-9]+)_secret_/;
export const STALE_CHECKOUT_RESULT = Symbol("stale-checkout-result");
export const UNCONFIRMED_REGISTRATION_RESULT = Symbol("unconfirmed-registration-result");

// Statuses the server sends only after it understood the request and refused it without a write,
// so a create may be retried. Any other failure — no status (network failure, timeout), a 5xx, or
// a 4xx a proxy can emit after the request reached the application (408) — may have saved the
// registration first and leaves the create unconfirmed.
const DEFINITIVE_REJECTION_STATUSES = new Set([
  HttpStatus.BAD_REQUEST,
  HttpStatus.UNAUTHORIZED,
  HttpStatus.FORBIDDEN,
  HttpStatus.NOT_FOUND,
  HttpStatus.CONFLICT,
  HttpStatus.GONE,
  HttpStatus.UNPROCESSABLE_ENTITY,
  HttpStatus.TOO_MANY_REQUESTS,
]);

/** Maps `registration[fields]…` FormData keys to flat review keys (e.g. `address[street]`). */
export function registrationFieldKeyFromFormKey(key) {
  if (!key.startsWith(KEY_FILTER)) {
    return null;
  }

  const remainder = key.slice(KEY_FILTER.length);
  const segments = [...remainder.matchAll(BRACKETED_FORM_KEY_SEGMENT_REGEX)].map((m) => m[1]);

  if (!segments.length) {
    return null;
  }

  return (
    segments[0] +
    segments
      .slice(1)
      .map((s) => `[${s}]`)
      .join("")
  );
}

const paymentTypes = {
  card: {
    name: "Credit Card",
  },
  paypal: {
    name: "Paypal",
  },
};

export const usePurchaseStore = (options = {}) =>
  defineScopedStore("purchase", CHECKOUT_SCOPE, () => {
    const api = options?.api ?? createPurchaseApi();

    const provider = ref();
    const organizationName = ref();
    const purchaseToken = ref();
    const environment = ref();
    const publishableKey = ref();
    const clientSecret = ref();
    const captureUrl = ref();
    const returnUrl = ref();
    const lineItems = ref();
    const expiresAt = ref();

    const extendExpirationUrl = ref();
    const applyPromoCodeUrl = ref();
    const removePromoCodeUrl = ref();

    const paymentEnabled = ref(false);
    const paymentRequired = ref(false);
    const paymentElement = ref();
    const aboutToConfirmPayment = ref(false);
    let checkoutGeneration = 0;
    let writeTail = Promise.resolve();
    const pendingWrites = ref(0);
    let addToCartPromise;
    let inFlightSubmit;
    let registrationUnconfirmed = false;

    const paymentType = ref();
    const paymentName = ref();

    const totalAmount = ref(0);
    const discountName = ref();
    const discountAmount = ref(0);
    const promoCode = ref();

    const entries = ref();
    const formElement = ref();
    const formResult = ref();
    const registrationUrl = ref();
    const formFields = ref();

    /**
     * The resource object provided as a prop to the custom element.
     * It represents the resource that is being purchased.
     * It is used to determine the type of payment and render the correct panels.
     *
     * @param {import("vue").Ref<Object>} resource
     */
    const resource = ref();

    function setResource(value) {
      resource.value = value;
    }

    watch(paymentElement, (element) => {
      useEventListener(element, "update-payment-type", ({ detail }) => {
        const [type] = detail;
        paymentType.value = type;
      });
    });

    watch(paymentEnabled, (enabled) => {
      paymentRequired.value = enabled;
    });

    watch(paymentType, (value) => {
      if (value) {
        const { name } = paymentTypes[value] ?? {};
        paymentName.value = name;
      }
    });

    // A validation that settles after a reset belongs to the previous checkout, whose payment
    // intent and return url are gone, so it is not reported.
    async function validatePayment() {
      const generation = checkoutGeneration;
      let result;

      try {
        result = await paymentElement.value.validate();
      } catch (error) {
        if (generation === checkoutGeneration) {
          reportPaymentFailure("validation", error, { thrown: true });
        }

        throw error;
      }

      if (result?.error && generation === checkoutGeneration) {
        reportPaymentFailure("validation", result.error);
      }

      return result;
    }

    async function confirmPayment() {
      const generation = checkoutGeneration;
      aboutToConfirmPayment.value = true;
      pendingWrites.value += 1;

      try {
        const result = await paymentElement.value.confirmPayment();
        if (generation !== checkoutGeneration) {
          return STALE_CHECKOUT_RESULT;
        }

        if (result?.error) {
          reportPaymentFailure("confirmation", result.error);
        }

        return result;
      } catch (error) {
        if (generation !== checkoutGeneration) {
          return STALE_CHECKOUT_RESULT;
        }

        reportPaymentFailure("confirmation", error, { thrown: true });
        throw error;
      } finally {
        pendingWrites.value -= 1;
        if (generation === checkoutGeneration) {
          aboutToConfirmPayment.value = false;
        }
      }
    }

    // Reports a thrown error, and a returned error unless the registrant caused it. The description
    // carries the Stripe error and the intent id, never the client secret or the publishable key;
    // Stripe's error messages do not echo card details. The page URL stays out: an RSVP page carries
    // the registration's bearer token in its query string.
    function reportPaymentFailure(step, error, { thrown = false } = {}) {
      if (!thrown && isRegistrantPaymentError(error)) {
        return;
      }

      reportClientError(paymentFailureMessage(step, error, thrown), {
        description: [
          `payment method: ${paymentType.value ?? "unknown"}`,
          `payment intent: ${stripeIntentId() ?? "unknown"}`,
          `error: ${paymentErrorDetail(error)}`,
        ].join(" — "),
        baseUrl: returnUrl.value,
        omitPage: true,
      });
    }

    function isRegistrantPaymentError(error) {
      return REGISTRANT_PAYMENT_ERROR_TYPES.has(error?.type) || REGISTRANT_PAYMENT_ERROR_CODES.has(error?.code);
    }

    function paymentFailureMessage(step, error, thrown) {
      if (error?.code === PaymentErrorCode.ELEMENT_READY_TIMEOUT) {
        return "Event registration checkout payment form never became ready";
      }

      if (thrown) {
        return `Event registration checkout payment ${step} threw`;
      }

      return `Event registration checkout payment ${step} returned an error`;
    }

    function paymentErrorDetail(error) {
      const parts = [error?.type ?? error?.name, error?.code, error?.decline_code, error?.message];
      return parts.filter(Boolean).join(" / ") || String(error);
    }

    function stripeIntentId() {
      return clientSecret.value?.match(STRIPE_INTENT_ID_REGEX)?.[1];
    }

    function createEntries(formData) {
      entries.value = Array.from(formData.keys()).reduce((acc, key) => {
        const nestedKey = registrationFieldKeyFromFormKey(key);

        if (nestedKey) {
          acc[nestedKey] = formData.get(key);
        }

        return acc;
      }, {});
    }

    function isValid() {
      prepareEligibilityValidation(formElement.value);

      formElement.value.reportValidity();
      return formElement.value.checkValidity();
    }

    const isAddingToCart = ref(false);
    let reportedDuplicateAdd = false;

    async function queueWrite(generation, write) {
      const previousWrite = writeTail;
      let release;
      writeTail = new Promise((resolve) => (release = resolve));
      pendingWrites.value += 1;

      try {
        await previousWrite;

        if (generation !== checkoutGeneration) {
          release();
          return STALE_CHECKOUT_RESULT;
        }

        return await write();
      } finally {
        release();
        pendingWrites.value -= 1;
      }
    }

    // Returns `true` on success, `false` on a failed request, and `null` when the cart already has
    // a purchase. Writes are queued so closing and reopening cannot start a second server write
    // while the prior cart or registration request is still in flight.
    async function addToCart(url) {
      if (addToCartPromise) {
        reportDuplicateAdd(url);
        return addToCartPromise;
      }

      const generation = checkoutGeneration;
      addToCartPromise = queueWrite(generation, async () => {
        // This cart already has a purchase. Adding again mints a second Purchase and a second payment
        // intent, while the payment form stays mounted against the first one — which is how a
        // registrant ends up charged for a purchase their registration is not attached to.
        //
        // The url is deliberately not part of the condition: a checkout is bound to a single event and
        // a purchase has only ever held one item, so a differing url means a duplicate call rather
        // than a second thing to buy. Comparing urls would let a duplicate through whenever the
        // caller varied the string at all.
        if (purchaseToken.value) {
          reportDuplicateAdd(url);
          return null;
        }

        isAddingToCart.value = true;

        try {
          const response = await api.addToCart(url);
          if (generation !== checkoutGeneration) {
            return null;
          }

          // A 2xx payload without a token would leave `hasPurchaseToken` false and the
          // loading overlay spinning with no retry — treat it as a failed request.
          if (!response?.purchase_token) {
            // Report the response *shape*, never its values — a well-formed payload carries
            // secrets (client_secret, publishable_key), so serializing the body would leak them.
            const shape = response && typeof response === "object" ? Object.keys(response).join(", ") : typeof response;
            reportClientError("Event registration checkout received no purchase token", {
              description: `${url} — 2xx response with no purchase_token (keys: ${shape})`,
              baseUrl: url,
            });
            return false;
          }

          purchaseToken.value = response.purchase_token;
          clientSecret.value = response.client_secret;
          captureUrl.value = response.capture_url;
          returnUrl.value = response.return_url;
          lineItems.value = response.line_items ?? [];
          expiresAt.value = new Date(response.expires_at).getTime();
          extendExpirationUrl.value = response.extend_expiration_url;
          applyPromoCodeUrl.value = response.apply_code_url ?? response.apply_promo_url;
          removePromoCodeUrl.value = response.remove_code_url ?? response.remove_promo_url;
          formFields.value = response.form_fields;
          registrationUrl.value = response.registration_url;

          provider.value = response.provider;
          environment.value = response.environment;
          publishableKey.value = response.publishable_key;
          organizationName.value = response.organization_name;

          updatePaymentValues(response);

          return true;
        } catch (err) {
          if (generation !== checkoutGeneration) {
            return null;
          }

          reportClientError("Event registration checkout failed to add to cart", {
            description: `${url} — ${err}`,
            baseUrl: url,
          });
          return false;
        } finally {
          isAddingToCart.value = false;
        }
      });

      try {
        const result = await addToCartPromise;
        return result === STALE_CHECKOUT_RESULT ? null : result;
      } finally {
        addToCartPromise = undefined;
      }
    }

    // What re-triggers add-to-cart has not been identified from the code, so the skip is reported to
    // surface it from real traffic — the report carries the page URL that should show the cause.
    // Reported once per checkout: roughly a quarter of purchases sit in a duplicate chain, so
    // reporting every skip would bury the signal in its own volume.
    function reportDuplicateAdd(url) {
      if (reportedDuplicateAdd) {
        return;
      }

      reportedDuplicateAdd = true;

      reportClientError("Event registration checkout skipped a duplicate add-to-cart", {
        description: `${url} — a purchase already exists for this cart`,
        baseUrl: url,
      });
    }

    /**
     * A flattened array of form fields, with the name transformed to a string with brackets
     * @type {import("vue").ComputedRef<Array<{ name: string, value: any }>>}
     */
    const formFieldsFlattened = computed(() => {
      const mapper = ({ name, value }) => ({ name: `[fields]${name}`, value });
      return flattenFormFields(formFields.value ?? {}).map(mapper);
    });

    /**
     * convert a deeply nested object to a flat array of the form [{ name: 'name', value: 'value' }]
     * the name is the path to the value in the object, transformed to a string with brackets
     * e.g. { address: { city: 'Berlin' } } => [{ name: '[address][city]', value: 'Berlin' }]
     *
     * @private
     * @param {Object} fields
     * @returns {Array<{ name: string, value: any }>}
     */
    function flattenFormFields(fields) {
      return Object.entries(fields).reduce((acc, [key, value]) => {
        if (typeof value === "object") {
          const mapper = ({ name, value }) => ({ name: `[${key}]${name}`, value });
          return [...acc, ...flattenFormFields(value).map(mapper)];
        }

        return [...acc, { name: `[${key}]`, value }];
      }, []);
    }

    const nowDate = useNow({ interval: 1000 });
    const now = computed(() => nowDate.value.getTime());

    const isExpired = computed(() => {
      if (!expiresAt.value) {
        return false;
      }

      return now.value > expiresAt.value;
    });

    const secondsUntilExpiration = computed(() => {
      if (isExpired.value) {
        return 0;
      }
      return Math.floor((expiresAt.value - now.value) / 1000);
    });

    const expiresWithinSeconds = computed(() => expiresAt.value && secondsUntilExpiration.value <= ONE_MINUTE);

    async function extendExpiration() {
      const generation = checkoutGeneration;
      const url = extendExpirationUrl.value;
      try {
        const response = await api.extendExpiration(url);
        if (generation !== checkoutGeneration) {
          return STALE_CHECKOUT_RESULT;
        }

        expiresAt.value = new Date(response.expires_at).getTime();
      } catch (error) {
        if (generation !== checkoutGeneration) {
          return STALE_CHECKOUT_RESULT;
        }

        throw error;
      }
    }

    // Resolves `true` on success, `null` when the server rejected the request (a create may be
    // retried), `UNCONFIRMED_REGISTRATION_RESULT` when a create failed without the server's answer,
    // and `STALE_CHECKOUT_RESULT` after a reset. An unconfirmed create may already be saved, so every
    // later submit in this checkout is refused until a reset starts a new purchase.
    async function submitForm() {
      const generation = checkoutGeneration;
      const formData = new FormData(formElement.value);
      createEntries(formData);

      formData.append("purchase_token", purchaseToken.value);
      formData.append("embedded_at_url", window.location.href);

      const previousSubmit = inFlightSubmit;
      const submission = queueWrite(generation, async () => {
        // A submit queued behind a failed create must not send another create: the failed request
        // may still have committed server-side (a lost response leaves no registration url behind),
        // and a second POST would register the same person twice. Only a completed create, which
        // recorded the registration url, lets a queued submit proceed as an update.
        if (previousSubmit) {
          const previousResult = await previousSubmit;
          if (previousResult !== true) {
            return previousResult;
          }
        }

        if (registrationUnconfirmed) {
          return UNCONFIRMED_REGISTRATION_RESULT;
        }

        const isChange = Boolean(registrationUrl.value);
        const url = isChange ? registrationUrl.value : formElement.value.action;

        try {
          const result = isChange ? await api.updateForm(url, formData) : await api.submitForm(url, formData);
          if (generation !== checkoutGeneration) {
            return STALE_CHECKOUT_RESULT;
          }

          // A create the server answered without a url may still have saved the registration: there
          // is nothing to change and a second POST could register the same person twice.
          if (!isChange && !result?.url) {
            registrationUnconfirmed = true;
            const shape = result && typeof result === "object" ? Object.keys(result).join(", ") : typeof result;
            reportClientError("Event registration checkout received no registration url", {
              description: `2xx response with no url (keys: ${shape})`,
              baseUrl: url,
              omitPage: true,
            });
            return UNCONFIRMED_REGISTRATION_RESULT;
          }

          formResult.value = result;
          if (!isChange) {
            registrationUrl.value = result.url;
          }
          updatePaymentValues(formResult.value);

          return true;
        } catch (err) {
          if (generation !== checkoutGeneration) {
            return STALE_CHECKOUT_RESULT;
          }

          reportSubmitFailure(isChange, url, err);
          if (!isChange && !isDefinitiveRejection(err)) {
            registrationUnconfirmed = true;
            return UNCONFIRMED_REGISTRATION_RESULT;
          }

          return null;
        }
      });

      inFlightSubmit = submission;
      const clearInFlightSubmit = () => {
        if (inFlightSubmit === submission) {
          inFlightSubmit = undefined;
        }
      };
      submission.then(clearInFlightSubmit, clearInFlightSubmit);

      return submission;
    }

    function isDefinitiveRejection(err) {
      return DEFINITIVE_REJECTION_STATUSES.has(err?.status);
    }

    function reportSubmitFailure(isChange, url, err) {
      reportClientError(`Event registration checkout failed to ${isChange ? "change" : "submit"} the registration`, {
        description: `status ${err?.status ?? "none"} - ${submitFailureDetail(err)}`,
        baseUrl: url,
        omitPage: true,
      });
    }

    function submitFailureDetail(err) {
      const body = err?.data;

      if (typeof body?.error === "string") {
        return body.error;
      }

      if (typeof body?.message === "string") {
        return body.message;
      }

      // Matches ApiClient's own message handling: an HTML error page can echo submitted form
      // values, so it stays out of the report.
      if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) {
        return body;
      }

      return "No safe error message supplied";
    }

    async function applyPromoCode(code) {
      const generation = checkoutGeneration;
      const url = applyPromoCodeUrl.value;
      discountAmount.value = 0;

      try {
        const result = await api.applyPromoCode(url, code);
        if (generation !== checkoutGeneration) {
          return { error: undefined };
        }

        const response = handlePromoApiResponse(result);
        promoCode.value = code;
        return response;
      } catch (err) {
        if (generation !== checkoutGeneration) {
          return { error: undefined };
        }

        reportPromoCodeFailure("apply", url, err);
        return handlePromoApiResponseError(err, "Unable to apply promo code");
      }
    }

    async function removePromoCode() {
      const generation = checkoutGeneration;
      const url = removePromoCodeUrl.value;

      try {
        const result = await api.removePromoCode(url);
        if (generation !== checkoutGeneration) {
          return { error: undefined };
        }

        const response = handlePromoApiResponse(result);
        promoCode.value = undefined;
        return response;
      } catch (err) {
        if (generation !== checkoutGeneration) {
          return { error: undefined };
        }

        reportPromoCodeFailure("remove", url, err);
        return handlePromoApiResponseError(err, "Unable to remove promo code");
      }
    }

    function handlePromoApiResponse(response) {
      updatePaymentValues(response);

      return { error: undefined };
    }

    function handlePromoApiResponseError(err, message) {
      return { error: err?.data?.error ?? message };
    }

    // A promo-code request that reaches the server and comes back a 4xx is an expected
    // business rejection (invalid/expired code) — high volume, not actionable, kept out
    // of Honeybadger. A transport failure (network/DNS/timeout has no HTTP status) or a
    // 5xx is an infrastructure problem worth tracking — the case where the user gets a
    // generic "Unable to apply promo code" with no server-side trace otherwise.
    function reportPromoCodeFailure(action, url, err) {
      if (err?.status && err.status < 500) {
        return;
      }

      reportClientError(`Event registration checkout failed to ${action} promo code`, {
        description: `${url} — ${err}`,
        baseUrl: url,
      });
    }

    function updatePaymentValues(values) {
      const { payment_required, payment_amount, discount_name, discount_amount } = values ?? {};

      paymentRequired.value = payment_required;
      totalAmount.value = payment_amount;
      discountName.value = discount_name;
      discountAmount.value = discount_amount;
    }

    function reset() {
      if (pendingWrites.value > 0) {
        return false;
      }

      checkoutGeneration += 1;
      purchaseToken.value = undefined;
      reportedDuplicateAdd = false;
      registrationUnconfirmed = false;
      formResult.value = undefined;
      formElement.value?.reset();
      formFields.value = undefined;
      expiresAt.value = undefined;
      registrationUrl.value = undefined;
      promoCode.value = undefined;
      paymentType.value = undefined;
      paymentName.value = undefined;
      paymentRequired.value = false;
      totalAmount.value = 0;
      discountName.value = undefined;
      discountAmount.value = 0;
      aboutToConfirmPayment.value = false;
      return true;
    }

    async function declineRsvp(url) {
      const generation = checkoutGeneration;
      return queueWrite(generation, () => api.declineRsvp(url));
    }

    return {
      provider,

      organizationName,
      environment,

      entries,
      resource,

      setFormElement: (element) => (formElement.value = element),

      get formElement() {
        return formElement.value;
      },

      get formResult() {
        return formResult.value;
      },

      formFields,
      formFieldsFlattened,

      isValid,
      addToCart,
      submitForm,
      applyPromoCode,
      removePromoCode,
      reset,

      purchaseToken,
      publishableKey,
      clientSecret,
      lineItems,
      captureUrl,
      returnUrl,

      expiresAt,
      isExpired,
      expiresWithinSeconds,
      secondsUntilExpiration,
      extendExpiration,

      paymentEnabled,
      paymentType,
      paymentName,

      paymentRequired,
      totalAmount,
      discountName,
      discountAmount,
      promoCode,

      setResource,

      setPaymentEnabled: (enabled) => (paymentEnabled.value = enabled),
      setPaymentElement: (element) => (paymentElement.value = unrefElement(element)),

      validatePayment,
      confirmPayment,
      aboutToConfirmPayment,

      declineRsvp,

      get hasPendingWrites() {
        return pendingWrites.value > 0;
      },

      get hasPurchaseToken() {
        return purchaseToken.value !== undefined;
      },

      get hasPaymentData() {
        return Boolean(paymentType.value) || Boolean(paymentRequired.value);
      },

      get hasDiscount() {
        return discountAmount.value;
      },
    };
  })();
